import Grid from '@mui/material/Grid';
import { Button, TextField } from '@mui/material';
import { useSetAtom } from 'jotai';
import { api } from '../../../../Api/Api';
import {
  AccessLevel,
  LocationConfiguration,
  PutLocationPayload,
} from '../../../../GeneratedClient';
import { useBackdrop } from '../../../../Hooks/useBackdrop';
import {
  organizationConfigurationEdited,
  useOrganizationConfiguration,
} from '../../../../Model/ConfigurationModel';
import { useRequiredSelectedLocationContext } from '../../../../Model/Data';
import { CTAutocomplete } from '../../../../Generic/Forms/CTAutocomplete';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { camelCaseToSpaces } from '../../../../Utilities/stringUtils';

export type AccessLevelData = {
  id?: string;
  name: string;
  organizationRoles: { title: string; value: string }[];
};

interface DrawerProps {
  onClose: () => void;
}

interface AddAccessLevelDrawerProps extends DrawerProps {
  data?: AccessLevelData;
  locationConfiguration: LocationConfiguration;
}

export function AddAccessLevel({
  data,
  locationConfiguration,
  onClose,
}: AddAccessLevelDrawerProps) {
  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = useForm({
    defaultValues: {
      id: data?.id || undefined,
      name: data?.name || '',
      organizationRoles: data?.organizationRoles || [],
    },
  });

  const organization = useOrganizationConfiguration();
  const { organizationId } = useRequiredSelectedLocationContext();

  const storeEdits = useSetAtom(organizationConfigurationEdited);

  const withBackdrop = useBackdrop();

  const save: SubmitHandler<AccessLevelData> = async (data) => {
    await withBackdrop(async () => {
      const newLocationConfiguration = new LocationConfiguration(
        locationConfiguration
      );

      // Remove any existing access level with the same name
      const filteredAccessLevels = (
        locationConfiguration?.accessLevels || []
      ).filter((al: AccessLevel) => al.id !== data.id);

      newLocationConfiguration.accessLevels = [
        ...filteredAccessLevels,
        new AccessLevel({
          id: data.id,
          name: data.name,
          organizationRoles: data.organizationRoles.map((role) => role.value),
          approvalRoles: [],
        }),
      ];

      const newConfig = await api.configuration.putLocationDefinition(
        organizationId!,
        new PutLocationPayload({
          locationConfiguration: newLocationConfiguration,
        })
      );

      storeEdits(newConfig);
      onClose();
    });
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{ maxWidth: 500 }}
      component="form" // This allows easier editing & submitting via the keyboard
      onSubmit={handleSubmit(save)}
    >
      <Grid size={12}>
        <h3>Add New Access Level</h3>
      </Grid>

      <Grid size={12}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              type="text"
              fullWidth
              required
              label="Name"
              placeholder="Enter a name for the access level"
              autoFocus
              {...field}
            />
          )}
        ></Controller>
      </Grid>

      <Grid size={12}>
        <CTAutocomplete
          name="organizationRoles"
          label="Organization Roles"
          fullWidth
          control={control}
          options={
            organization?.roles?.map(({ roleName }) => ({
              title: camelCaseToSpaces(roleName!),
              value: roleName!,
            })) || []
          }
        />
      </Grid>

      <Grid size={12} sx={{ textAlign: 'right' }}>
        <Button
          color="secondary"
          variant="contained"
          sx={{ marginRight: 2 }}
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          color="primary"
          variant="contained"
          disabled={!isDirty}
        >
          Save
        </Button>
      </Grid>
    </Grid>
  );
}
